import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  BarChart3,
  TrendingUp,
  Shield,
  Users,
  FileText,
  Zap,
  Check,
  ArrowRight,
  ShieldCheck,
  Package,
  Microscope,
  CheckCircle,
  LineChart,
  Globe,
  Bell,
  Lock,
  Database,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import logo from '../Logo.svg';
import { toast } from 'sonner';
import apiClient from '../services/api';

const EMPTY_TRIAL_FORM = { firstName: '', lastName: '', email: '', phone: '', organization: '' };

export default function HomePage() {
  const navigate = useNavigate();

  const [isTrialOpen, setIsTrialOpen] = useState(false);
  const [trialForm, setTrialForm] = useState(EMPTY_TRIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  const openTrialDialog = () => {
    setTrialForm(EMPTY_TRIAL_FORM);
    setSubmitted(false);
    setSubmittedData(null);
    setIsTrialOpen(true);
  };

  const handleTrialSubmit = async () => {
    if (!trialForm.firstName || !trialForm.lastName || !trialForm.email) {
      toast.error('First name, last name and email are required.');
      return;
    }
    setIsSubmitting(true);
    try {
      await apiClient.post('/trial-requests', {
        firstName:    trialForm.firstName,
        lastName:     trialForm.lastName,
        email:        trialForm.email,
        phone:        trialForm.phone || undefined,
        organization: trialForm.organization || undefined,
      });
      setSubmittedData({ ...trialForm });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const setField = (key, value) => setTrialForm(p => ({ ...p, [key]: value }));

  const features = [
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Real-Time Dashboard',
      description: 'Monitor all cannabis operations with comprehensive KPIs including compliance score, inventory value, and license status at a glance.',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
    },
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: 'License Management',
      description: 'Track all operator licenses with expiration alerts, automatic renewal reminders, and complete document management system.',
      color: 'text-green-700',
      bgColor: 'bg-green-50',
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: 'Seed-to-Sale Inventory',
      description: 'Complete inventory tracking from cultivation to sale with batch management, stock alerts, and automated COA integration.',
      color: 'text-teal-700',
      bgColor: 'bg-teal-50',
    },
    {
      icon: <Microscope className="w-6 h-6" />,
      title: 'Lab & COA Management',
      description: 'Track test results, certificates of analysis, and safety compliance for microbials, pesticides, and heavy metals.',
      color: 'text-lime-700',
      bgColor: 'bg-lime-50',
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Compliance Reports',
      description: 'Generate and submit state-required reports with audit trails, submission tracking, and automated deadline management.',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Advanced Analytics',
      description: 'Year-over-year comparisons, state performance metrics, inventory turnover rates, and comprehensive business intelligence.',
      color: 'text-green-700',
      bgColor: 'bg-green-50',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Operator Directory',
      description: 'Search, filter, and manage cannabis operators across multiple states with advanced sorting and export capabilities.',
      color: 'text-teal-700',
      bgColor: 'bg-teal-50',
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: 'Smart Alerts',
      description: 'Real-time notifications for license expirations, low inventory, missing COAs, and compliance report deadlines.',
      color: 'text-lime-700',
      bgColor: 'bg-lime-50',
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Multi-State Coverage',
      description: 'Operate across up to 50 states with unified reporting, region-specific compliance rules, and consolidated dashboards.',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
    },
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Connect Your Operations',
      description: 'Onboard your cannabis operators, upload licenses, and configure your state-specific compliance requirements in minutes.',
      icon: <Database className="w-7 h-7" />,
    },
    {
      step: '02',
      title: 'Monitor & Stay Compliant',
      description: 'Get real-time dashboards, automated alerts for upcoming deadlines, and instant visibility into your compliance health score.',
      icon: <Shield className="w-7 h-7" />,
    },
    {
      step: '03',
      title: 'Report with Confidence',
      description: 'Generate state-mandated reports, export data to Excel, and submit with a complete audit trail — all from one place.',
      icon: <CheckCircle className="w-7 h-7" />,
    },
  ];

  const complianceFeatures = [
    {
      title: 'State Reporting',
      items: ['Monthly compliance reports', 'Inventory reconciliation', 'Waste disposal logs', 'Financial reporting'],
    },
    {
      title: 'License Tracking',
      items: ['Expiration monitoring', 'Renewal reminders', 'Document storage', 'Multi-state support'],
    },
    {
      title: 'Quality Control',
      items: ['COA management', 'Lab result tracking', 'Safety compliance', 'Batch testing records'],
    },
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '$35',
      priceYearly: '$350',
      description: 'Perfect for small operators just getting started',
      features: [
        '250 Operators',
        '1 State',
        'Compliance Reports',
        'License Management',
        'Basic Analytics',
        'Email Support',
      ],
      popular: false,
    },
    {
      name: 'Growth',
      price: '$105',
      priceYearly: '$1,050',
      description: 'For growing multi-state operations',
      features: [
        '750 Operators',
        '3 States',
        'Compliance Reports',
        'Excel Export',
        'Multi-State Access',
        'Multiple Users',
        'Audit Trail',
        'Priority Support',
      ],
      popular: true,
    },
    {
      name: 'Enterprise',
      price: '$205',
      priceYearly: '$2,050',
      description: 'Full power for large-scale operations',
      features: [
        '1250 Operators',
        '50 States',
        'All Compliance Reports',
        'Advanced Excel Export',
        'Full Multi-State Access',
        'Unlimited Users',
        'Complete Audit Trail',
        'AI Insights',
        '24/7 Dedicated Support',
      ],
      popular: false,
    },
  ];

  const stats = [
    { value: '500+', label: 'Cannabis Operators' },
    { value: '15', label: 'States Supported' },
    { value: '94.5%', label: 'Avg Compliance Score' },
    { value: '24/7', label: 'Real-Time Monitoring' },
  ];


  return (
    <div className="min-h-screen bg-background">
      <Navbar isLanding={true} onTrialClick={openTrialDialog} />

      {/* ── Hero Section ── */}
      <section className="gradient-hero py-20 sm:py-32 relative overflow-hidden">
        {/* Decorative background circles */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-4xl mx-auto text-center">
            {/* Logo in hero */}
            <div className="flex justify-center mb-8 animate-fade-in">
              <img src={logo} alt="Verdionix" className="h-20 sm:h-24 w-auto" style={{ mixBlendMode: 'multiply' }} />
            </div>
            <Badge className="mb-6 animate-fade-in" variant="secondary">
              <CheckCircle className="w-3 h-3 mr-1" />
              Trusted by 500+ Cannabis Companies
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6 animate-fade-in leading-tight">
              Cannabis Compliance,{' '}
              <span className="text-primary">Simplified.</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-4 max-w-2xl mx-auto animate-fade-in">
              Directory. Compliance. Intelligence — all in one platform. Manage licenses, track inventory from seed-to-sale, ensure lab compliance, and generate state reports with confidence.
            </p>
            <p className="text-base font-medium text-primary mb-10 animate-fade-in">
              Stay compliant. Stay ahead.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in">
              <Button size="lg" onClick={openTrialDialog} className="shadow-glow gap-2">
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats Section ── */}
      <section className="py-14 bg-primary/5 border-y border-primary/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-20 sm:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" variant="secondary">
              <Zap className="w-3 h-3 mr-1" />
              How It Works
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              Up and Running in Minutes
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Three simple steps to full cannabis compliance coverage
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative flex flex-col items-center text-center group">
                {/* Connector line between steps */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[calc(50%+3rem)] w-[calc(100%-6rem)] h-px bg-primary/20" />
                )}
                <div className="w-20 h-20 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-5 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 shadow-sm">
                  {step.icon}
                </div>
                <span className="text-4xl font-bold text-primary/20 font-display mb-2">{step.step}</span>
                <h3 className="text-lg font-semibold text-foreground mb-3">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features Section ── */}
      <section id="features" className="py-20 sm:py-32 bg-muted/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" variant="secondary">
              <Zap className="w-3 h-3 mr-1" />
              Comprehensive Features
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Everything You Need for Cannabis Compliance
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From license management to lab testing, inventory tracking to state reporting — all in one powerful platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="hover:shadow-xl transition-all duration-300 animate-fade-in border border-border/60 hover:border-primary/30 bg-card"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CardHeader>
                  <div className={`w-12 h-12 rounded-xl ${feature.bgColor} flex items-center justify-center mb-4 ${feature.color}`}>
                    {feature.icon}
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                  <CardDescription className="text-sm leading-relaxed">{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── Data Research & AI Insights Hero ── */}
      <section className="py-20 sm:py-28 bg-gradient-to-br from-[#0a1f14] via-[#0d2b1a] to-[#0a1f14] text-white overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-emerald-600/20 text-emerald-300 border-emerald-500/30">
              <Zap className="w-3 h-3 mr-1" />
              Intelligence Layer
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4">
              Data & AI-Powered Intelligence
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Go beyond compliance. Leverage market data and machine learning to stay ahead of the industry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Data Research Service */}
            <div className="rounded-2xl border border-emerald-500/20 bg-white/5 backdrop-blur-sm p-8 hover:bg-white/10 transition-all group">
              <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6 group-hover:bg-emerald-500/30 transition-colors">
                <Database className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-display font-semibold mb-3 text-white">Data Research Service</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Access curated cannabis market data, operator intelligence, and state-level research reports to power smarter business decisions.
              </p>
              <ul className="space-y-2">
                {['Operator intelligence database', 'State market reports', 'License activity tracking', 'Competitive benchmarking'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* AI Insights */}
            <div className="rounded-2xl border border-emerald-500/20 bg-white/5 backdrop-blur-sm p-8 hover:bg-white/10 transition-all group">
              <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6 group-hover:bg-emerald-500/30 transition-colors">
                <Zap className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-display font-semibold mb-3 text-white">AI-Based Industry Insights</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Leverage machine learning to surface compliance risks, predict license renewal needs, and uncover actionable market trends before they impact your operations.
              </p>
              <ul className="space-y-2">
                {['Compliance risk scoring', 'License renewal predictions', 'Market trend analysis', 'Automated anomaly detection'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg gap-2"
              onClick={() => navigate('/register')}
            >
              Get Full Intelligence Access <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ── Compliance Feature Detail ── */}
      <section id="compliance" className="py-20 sm:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
                Built for Cannabis Compliance
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Industry-specific features designed to keep you compliant with state regulations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {complianceFeatures.map((section, index) => (
                <Card key={index} className="shadow-md border-primary/10 hover:border-primary/30 transition-colors">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-primary" />
                      {section.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {section.items.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-primary" />
                          </div>
                          <span className="text-sm text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing Section ── */}
      <section id="pricing" className="py-20 sm:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" variant="secondary">
              <LineChart className="w-3 h-3 mr-1" />
              Flexible Pricing
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Choose Your Plan
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Scalable pricing for cannabis operations of all sizes. Start small, grow as you expand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card
                key={index}
                className={`relative flex flex-col hover:shadow-xl transition-all duration-300 ${
                  plan.popular
                    ? 'border-primary shadow-glow scale-105 bg-gradient-to-b from-primary/5 to-card'
                    : 'border-border/60'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="shadow-lg px-4 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                    <span className="text-muted-foreground">/month</span>
                    {plan.priceYearly && (
                      <div className="text-sm text-muted-foreground mt-1">
                        or <span className="font-medium text-foreground">{plan.priceYearly}</span>/year
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex-grow flex flex-col">
                  <ul className="space-y-3 mb-6 flex-grow">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full"
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => navigate('/register')}
                  >
                    Get Started <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── Security Trust Bar ── */}
      <section className="py-12 bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16 text-center">
            {[
              { icon: <Lock className="w-5 h-5" />, label: 'SOC 2 Compliant' },
              { icon: <Shield className="w-5 h-5" />, label: 'Bank-Level Encryption' },
              { icon: <Globe className="w-5 h-5" />, label: '50-State Coverage' },
              { icon: <CheckCircle className="w-5 h-5" />, label: '99.9% Uptime SLA' },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-muted-foreground">
                <span className="text-primary">{item.icon}</span>
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="py-20 sm:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-primary/10 via-card to-primary/5 rounded-3xl border border-primary/20 shadow-xl p-12">
            <img src={logo} alt="Verdionix" className="h-14 w-auto mx-auto mb-6" style={{ mixBlendMode: 'multiply' }} />
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              Ready to Simplify Your Cannabis Compliance?
            </h2>
            <p className="text-lg text-muted-foreground mb-2 max-w-xl mx-auto">
              Join hundreds of cannabis operators already using Verdionix to streamline compliance, manage inventory, and ensure regulatory adherence across multiple states.
            </p>
            <p className="text-base font-semibold text-primary mb-8">
              Compliance made simple. Growth made possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={openTrialDialog} className="shadow-glow gap-2">
                Start Your Free Trial <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* ── Free Trial Dialog ── */}
      <Dialog
        open={isTrialOpen}
        onOpenChange={open => {
          setIsTrialOpen(open);
          if (!open) { setSubmitted(false); setSubmittedData(null); }
        }}
      >
        <DialogContent className="max-w-md">
          {!submitted ? (
            <>
              <DialogHeader>
                <DialogTitle>Start Your Free Trial</DialogTitle>
                <DialogDescription>
                  Tell us a bit about yourself and we'll set up your trial account.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-1">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>First Name *</Label>
                    <Input
                      placeholder="Jane"
                      value={trialForm.firstName}
                      onChange={e => setField('firstName', e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Last Name *</Label>
                    <Input
                      placeholder="Smith"
                      value={trialForm.lastName}
                      onChange={e => setField('lastName', e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    placeholder="jane@company.com"
                    value={trialForm.email}
                    onChange={e => setField('email', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input
                    placeholder="(555) 000-0000"
                    value={trialForm.phone}
                    onChange={e => setField('phone', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Organization</Label>
                  <Input
                    placeholder="Your company name"
                    value={trialForm.organization}
                    onChange={e => setField('organization', e.target.value)}
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button className="flex-1" onClick={handleTrialSubmit} disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {isSubmitting ? 'Submitting...' : 'Request Trial'}
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => setIsTrialOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Thank You, {submittedData?.firstName}!</DialogTitle>
                <DialogDescription>Your trial request has been received.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2 text-sm">
                  <p><span className="font-medium text-green-800">Name:</span>{' '}
                    <span className="text-green-700">{submittedData?.firstName} {submittedData?.lastName}</span></p>
                  <p><span className="font-medium text-green-800">Email:</span>{' '}
                    <span className="text-green-700">{submittedData?.email}</span></p>
                  {submittedData?.phone && (
                    <p><span className="font-medium text-green-800">Phone:</span>{' '}
                      <span className="text-green-700">{submittedData.phone}</span></p>
                  )}
                  {submittedData?.organization && (
                    <p><span className="font-medium text-green-800">Organization:</span>{' '}
                      <span className="text-green-700">{submittedData.organization}</span></p>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Our team will review your request and send trial account credentials to{' '}
                  <strong>{submittedData?.email}</strong> shortly.
                </p>
                <Button className="w-full" onClick={() => setIsTrialOpen(false)}>
                  Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
